// График любой величины замера на фоне коридора нормы (границы последнего замера);
// переключатель «+ объём тренировок» добавляет недельный тоннаж второй осью.

import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { api } from '@/api/client'
import { TimeSeriesChart, type ChartSeries } from '@/components/charts/TimeSeriesChart'
import { Card, ErrorText, Select } from '@/components/ui'
import { todayIso, type Period } from '@/lib/periods'
import type { Volume } from '@/pages/plan/api'

import { useInBodySeries, type ScanField, type ScanOut } from '../api'
import { FIELD_LABELS } from '../labels'
import { bandFor, CHART_FIELDS } from './chartFields'

const WEEKS = 52

function useWeeklyTonnage(enabled: boolean) {
  const today = todayIso()
  return useQuery({
    queryKey: ['plan', 'tonnage', today],
    queryFn: () => api.get<Volume>('/plan/volume', { query: { week_start: today, weeks: WEEKS } }),
    enabled,
  })
}

function optionLabel(field: ScanField): string {
  const meta = FIELD_LABELS[field]
  return meta.unit ? `${meta.label}, ${meta.unit}` : meta.label
}

export function ChartCard({ latest }: { latest: ScanOut | null }) {
  const [field, setField] = useState<ScanField>('weight_kg')
  const [period, setPeriod] = useState<Period>('year')
  const [withVolume, setWithVolume] = useState(false)
  const points = useInBodySeries(field, {})
  const tonnage = useWeeklyTonnage(withVolume)

  const meta = FIELD_LABELS[field]
  const weeks = withVolume && tonnage.data ? [...tonnage.data.previous, tonnage.data.current] : null
  const series: ChartSeries[] = (() => {
    const main: ChartSeries = {
      key: field,
      label: meta.label,
      unit: meta.unit ?? undefined,
      digits: meta.digits,
      axis: 'left',
      points: (points.data ?? []).map((p) => ({ x: p.day, y: p.value })),
    }
    if (!weeks) return [main]
    return [
      main,
      {
        key: 'tonnage',
        label: 'Тоннаж за неделю',
        unit: 'кг',
        digits: 0,
        axis: 'right',
        points: weeks.map((w) => ({ x: w.week_start, y: w.tonnage_kg })),
      },
    ]
  })()

  const band = bandFor(field, latest)

  return (
    <Card title="Динамика" subtitle={band ? 'Заливка — коридор нормы по последнему замеру' : undefined}>
      <div className="mb-3 flex flex-wrap items-end gap-x-4 gap-y-2">
        <Select
          label="Величина"
          value={field}
          onChange={(event) => setField(event.target.value as ScanField)}
          options={CHART_FIELDS.map((f) => ({ value: f, label: optionLabel(f) }))}
          containerClassName="w-60 max-w-full"
        />
        <label className="flex min-h-11 items-center gap-2 text-sm text-fg lg:min-h-10">
          <input
            type="checkbox"
            checked={withVolume}
            onChange={(event) => setWithVolume(event.target.checked)}
            className="h-4 w-4 accent-[var(--accent)]"
          />
          + объём тренировок
        </label>
      </div>
      {points.error ? <ErrorText error={points.error} /> : null}
      <TimeSeriesChart
        series={series}
        band={band}
        period={period}
        onPeriodChange={setPeriod}
        emptyText="Нет замеров за этот период"
      />
    </Card>
  )
}
